import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { contentProjectsAPI, taxonomyAPI, contentPOsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogAction, AlertDialogCancel,
} from '../components/ui/alert-dialog';
import {
  ArrowLeft, Pencil, Trash2, FolderKanban, CalendarClock, Receipt, User, Layers, AlertCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { ContentProjectFormDialog } from '../components/content-items/ContentProjectFormDialog';
import { ProjectStatusBar } from '../components/content-items/ProjectStatusBar';
import { CATALOG_STATUS_STYLES, MS_PRIORITY_STYLES, TRACK_OPTIONS } from './ContentItemsPage';

export const ContentProjectDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [taxonomyMap, setTaxonomyMap] = useState({});
  const [readinessOptions, setReadinessOptions] = useState(TRACK_OPTIONS);
  const [msOwnerOptions, setMsOwnerOptions] = useState([]);
  const [pmOwnerOptions, setPmOwnerOptions] = useState([]);
  const [pos, setPos] = useState([]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await contentProjectsAPI.getById(id);
      setProject(data);
    } catch { toast.error('Failed to load project'); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    taxonomyAPI.getAll().then(cats => {
      const map = {};
      cats.forEach(c => { map[c.id] = c.items.filter(i => i.active); });
      setTaxonomyMap(map);
      setMsOwnerOptions(map.ms_owner || []);
      setPmOwnerOptions(map.pmo || map.pm_owner || []);
      if (map.readiness_stage?.length) setReadinessOptions(map.readiness_stage);
    }).catch(() => {});
    contentPOsAPI.getActive().then(setPos).catch(() => {});
  }, []);

  const ownerLabel = (key, value) => (taxonomyMap[key] || []).find(i => i.value === value)?.label || value;
  const readinessLabel = (v) => readinessOptions.find(t => t.value === v)?.label || v;

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await contentProjectsAPI.delete(id);
      toast.success('Project deleted');
      navigate('/content-items?tab=projects');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete project');
      setDeleteOpen(false);
    } finally { setDeleting(false); }
  };

  if (loading || !project) {
    return <div className="space-y-4"><div className="h-24 bg-muted animate-pulse rounded-lg" /><div className="h-64 bg-muted animate-pulse rounded-lg" /></div>;
  }

  const items = project.items || [];
  const isOverdue = project.target_delivery_date && project.target_delivery_date < new Date().toISOString().slice(0, 10) && (project.status_rollup?.in_build || 0) > 0;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="content-project-detail-page">
      <Button variant="ghost" size="sm" onClick={() => navigate('/content-items?tab=projects')} data-testid="back-to-projects-btn">
        <ArrowLeft className="w-4 h-4 mr-2" />Back to Projects
      </Button>

      <Card>
        <CardContent className="pt-5">
          <div className="flex flex-col md:flex-row justify-between gap-4">
            <div className="space-y-2 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <FolderKanban className="w-6 h-6 text-lime-600" />
                <h1 className="text-2xl font-heading font-bold" data-testid="content-project-name">{project.name}</h1>
                <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200" data-testid="content-project-item-count">{project.item_count} item{project.item_count !== 1 ? 's' : ''}</Badge>
                {isOverdue && <Badge className="bg-red-100 text-red-700 border-red-200" data-testid="content-project-overdue-badge"><AlertCircle className="w-3 h-3 mr-1" />Overdue</Badge>}
              </div>
              {project.description && <p className="text-sm text-muted-foreground">{project.description}</p>}
              <div className="flex items-center gap-4 flex-wrap text-sm text-muted-foreground pt-1">
                {project.ms_owner && <span className="flex items-center gap-1.5" data-testid="content-project-msowner"><User className="w-3.5 h-3.5" />MS Owner: <span className="text-foreground">{ownerLabel('ms_owner', project.ms_owner)}</span></span>}
                {project.pm_owner && <span className="flex items-center gap-1.5" data-testid="content-project-pmowner"><User className="w-3.5 h-3.5" />PM Owner: <span className="text-foreground">{pmOwnerOptions.find(o => o.value === project.pm_owner)?.label || project.pm_owner}</span></span>}
                {project.target_delivery_date && <span className="flex items-center gap-1.5" data-testid="content-project-target-date"><CalendarClock className="w-3.5 h-3.5" />Target: <span className="text-foreground">{project.target_delivery_date}</span></span>}
                {project.linked_po_number && <span className="flex items-center gap-1.5" data-testid="content-project-po"><Receipt className="w-3.5 h-3.5" />PO: <span className="text-foreground">{project.linked_po_number}</span></span>}
                <span>Created {new Date(project.created_at).toLocaleDateString()}</span>
              </div>
              {project.item_count > 0 && (
                <div className="pt-2 max-w-xs" data-testid="content-project-status-rollup">
                  <ProjectStatusBar rollup={project.status_rollup} />
                </div>
              )}
            </div>
            <div className="flex items-start gap-2 flex-shrink-0">
              <Button variant="outline" size="sm" onClick={() => setEditOpen(true)} data-testid="edit-content-project-btn"><Pencil className="w-3.5 h-3.5 mr-2" />Edit</Button>
              <Button variant="outline" size="sm" onClick={() => setDeleteOpen(true)} className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid="delete-content-project-btn"><Trash2 className="w-3.5 h-3.5 mr-2" />Delete</Button>
            </div>
          </div>
          {project.notes_html && (
            <div className="mt-4 pt-4 border-t border-border">
              <p className="text-xs font-medium text-muted-foreground mb-2">Notes</p>
              <div className="rich-text-content text-sm" data-testid="content-project-notes" dangerouslySetInnerHTML={{ __html: project.notes_html }} />
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Content Items in this Project ({items.length})</CardTitle>
        </CardHeader>
        <CardContent className="pt-3">
          {items.length === 0 ? (
            <div className="text-center py-12">
              <Layers className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">No content items assigned to this project yet.</p>
              <p className="text-xs text-muted-foreground mt-1">Assign items from the ContentDev Tracker (bulk "Assign to Project" or the item's edit form).</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Course</TableHead>
                  <TableHead>Solution Area</TableHead>
                  <TableHead>Asset Type</TableHead>
                  <TableHead>Readiness Stage</TableHead>
                  <TableHead>MS Priority</TableHead>
                  <TableHead>Current Phase</TableHead>
                  <TableHead>Catalog Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map(item => (
                  <TableRow key={item.id} className="cursor-pointer hover:bg-muted/50" data-testid={`project-item-row-${item.id}`} onClick={() => navigate(`/content-items/${item.id}`)}>
                    <TableCell className="font-medium text-sm max-w-[260px] truncate">{item.title}</TableCell>
                    <TableCell className="text-sm">{item.solution_area || <span className="text-xs text-muted-foreground">—</span>}</TableCell>
                    <TableCell className="text-sm">{item.asset_type || <span className="text-xs text-muted-foreground">—</span>}</TableCell>
                    <TableCell className="text-sm">{readinessLabel(item.content_track)}</TableCell>
                    <TableCell>{item.ms_priority ? <Badge className={MS_PRIORITY_STYLES[item.ms_priority]}>{item.ms_priority}</Badge> : <span className="text-xs text-muted-foreground">—</span>}</TableCell>
                    <TableCell className="text-sm">{item.current_phase?.label}</TableCell>
                    <TableCell><Badge className={CATALOG_STATUS_STYLES[item.catalog_status]?.bg}>{CATALOG_STATUS_STYLES[item.catalog_status]?.label}</Badge></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <ContentProjectFormDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        initial={project}
        msOwnerOptions={msOwnerOptions}
        pmOwnerOptions={pmOwnerOptions}
        pos={pos}
        currentUser={user}
        onSaved={load}
      />

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent data-testid="content-project-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{project.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              This deletes the project. Its {project.item_count} content item{project.item_count !== 1 ? 's' : ''} will NOT be deleted — they'll simply become unassigned (standalone).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="content-project-delete-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={deleting} className="bg-red-600 hover:bg-red-700" data-testid="content-project-delete-confirm-btn">
              {deleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ContentProjectDetailPage;
